import type { ProductInfo } from "@artis/sdk";
import { useProducts } from "@artis/sdk-react";
import { deleteCharBackward, deleteCharForward, history, historyKeymap } from "@codemirror/commands";
import { Compartment, EditorState, Prec, type Range } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  keymap,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  applyCompletion,
  completionTarget,
  curveArgCompletion,
  curveCallArgs,
  matchFunctions,
  signatureContext,
  spliceArg,
} from "./formula/complete";
import { fuzzyMatch } from "./formula/fuzzy";
import { FUNCTIONS } from "./formula/functions";
import { segmentFormula } from "./formula/highlight";

const FUNCTION_NAMES = Object.keys(FUNCTIONS);
const MAX_PRODUCT_ITEMS = 50;

type CompletionItem = {
  key: string;
  badge?: string;
  label: string;
  labelIndices?: number[];
  detail?: string;
  detailIndices?: number[];
  apply: () => { text: string; caret: number };
};

function Highlighted({ text, indices }: { text: string; indices?: number[] }) {
  if (!indices?.length) return <>{text}</>;
  const parts: ReactNode[] = [];
  let pos = 0;
  let i = 0;
  while (i < indices.length) {
    let j = i;
    while (j + 1 < indices.length && indices[j + 1] === indices[j]! + 1) j++;
    const start = indices[i]!;
    const end = indices[j]! + 1;
    if (start > pos) parts.push(text.slice(pos, start));
    parts.push(
      <span key={start} className="rounded-sm bg-yellow-200">
        {text.slice(start, end)}
      </span>,
    );
    pos = end;
    i = j + 1;
  }
  if (pos < text.length) parts.push(text.slice(pos));
  return <>{parts}</>;
}

export type CommitMovement = readonly [-1 | 0 | 1, -1 | 0 | 1];

export type FormulaInputHandle = {
  focus: () => void;
  blur: () => void;
  setSelectionRange: (start: number, end: number) => void;
};

interface FormulaInputProps {
  value: string;
  disabled?: boolean;
  className?: string;
  inputRef?: (el: FormulaInputHandle | null) => void;
  onChange?: (value: string) => void;
  onCaretChange?: (caret: number) => void;
  onCommit?: (movement?: CommitMovement) => void;
  onCancel?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const CHIP_BG: Record<"product" | "field", string> = {
  product: "bg-[var(--colour-surface-container-default)]",
  field: "bg-[#2173461a]",
};

class ChipWidget extends WidgetType {
  constructor(
    readonly name: string,
    readonly kind: "product" | "field",
  ) {
    super();
  }

  override eq(other: ChipWidget) {
    return other.name === this.name && other.kind === this.kind;
  }

  toDOM() {
    const el = document.createElement("span");
    el.className = `mx-0.5 rounded-full border border-[var(--colour-border-default)] px-1.5 text-[var(--colour-text-default)] ${CHIP_BG[this.kind]}`;
    el.textContent = this.name;
    return el;
  }
}

function buildDecorations(text: string, products: readonly ProductInfo[]) {
  const marks: Range<Decoration>[] = [];
  const chips: Range<Decoration>[] = [];
  for (const s of segmentFormula(text)) {
    const from = s.start;
    const to = s.start + s.text.length;
    if (to <= from) continue;
    if (s.pill) {
      const closed = s.text.length > 2 && s.text.endsWith('"');
      const inner = s.text.slice(1, closed ? -1 : undefined);
      if (s.pill === "product") {
        const product = products.find((p) => p.id === inner);
        if (product) {
          chips.push(Decoration.replace({ widget: new ChipWidget(product.name, "product") }).range(from, to));
        } else {
          marks.push(
            Decoration.mark({
              class:
                "rounded border border-[var(--colour-border-default)] bg-[var(--colour-surface-container-default)]",
            }).range(from, to),
          );
        }
      } else if (closed) {
        chips.push(Decoration.replace({ widget: new ChipWidget(inner, "field") }).range(from, to));
      }
    } else if (s.color) {
      marks.push(Decoration.mark({ attributes: { style: `color: ${s.color}` } }).range(from, to));
    }
  }
  return {
    decorations: Decoration.set([...marks, ...chips], true),
    atomics: Decoration.set(chips, true),
  };
}

const GRID_FONT =
  "Inter, Roboto, -apple-system, BlinkMacSystemFont, avenir next, avenir, segoe ui, helvetica neue, helvetica, Ubuntu, noto, arial, sans-serif";

const THEME = EditorView.theme({
  "&": { height: "100%", backgroundColor: "transparent", fontSize: "12px", color: "inherit" },
  "&.cm-focused": { outline: "none" },
  ".cm-scroller": {
    fontFamily: GRID_FONT,
    alignItems: "center !important",
    overflow: "hidden",
  },
  ".cm-content": { minHeight: "unset", padding: "0 8px", caretColor: "currentColor", whiteSpace: "pre" },
  ".cm-line": { padding: "0" },
});

export default function FormulaInput({
  value,
  disabled = false,
  className = "",
  inputRef,
  onChange,
  onCaretChange,
  onCommit,
  onCancel,
  onFocus,
  onBlur,
}: FormulaInputProps) {
  const [caret, setCaret] = useState(0);
  const [focused, setFocused] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [dismissedPrefix, setDismissedPrefix] = useState<string | null>(null);

  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const editableRef = useRef(new Compartment());
  const syncingRef = useRef(false);

  const products = useProducts({ kind: "curve" });

  const curveTarget = focused && !disabled ? curveArgCompletion(value, caret) : null;
  const fnTarget = !curveTarget && focused && !disabled ? completionTarget(value, caret) : null;
  const activePrefix = curveTarget?.prefix ?? fnTarget?.prefix ?? null;
  const dismissed = activePrefix !== null && activePrefix === dismissedPrefix;

  const curveParamCount = FUNCTIONS.CURVE!.params.length;
  const spliceQuoted = (span: { start: number; end: number }, inner: string, argIndex: number) => () =>
    spliceArg(value, span, inner, argIndex >= curveParamCount - 1 ? ")" : ",");

  let items: CompletionItem[] = [];
  if (!dismissed) {
    if (curveTarget?.arg === "product") {
      const q = curveTarget.prefix;
      items = products
        .flatMap((p) => {
          const name = fuzzyMatch(q, p.name);
          const desc = p.description ? fuzzyMatch(q, p.description) : null;
          if (!name && !desc) return [];
          return [{ p, name, desc, score: Math.max(name?.score ?? -1, desc ? desc.score - 1 : -1) }];
        })
        .sort((a, b) => b.score - a.score || a.p.name.localeCompare(b.p.name))
        .slice(0, MAX_PRODUCT_ITEMS)
        .map(({ p, name, desc }) => ({
          key: p.id,
          label: p.name,
          labelIndices: name?.indices,
          detail: p.description ?? p.package,
          detailIndices: p.description ? desc?.indices : undefined,
          apply: spliceQuoted(curveTarget, p.id, 0),
        }));
    } else if (curveTarget?.arg === "field") {
      const product = products.find((p) => p.id === curveTarget.productId);
      const fields = [...new Set([product?.defaultField, ...(product?.fields ?? [])])].filter(
        (f): f is string => typeof f === "string" && f.length > 0,
      );
      const list = fields.length > 0 ? fields : ["value"];
      const q = curveTarget.prefix;
      items = list
        .flatMap((f) => {
          const m = fuzzyMatch(q, f);
          return m ? [{ f, m }] : [];
        })
        .sort((a, b) => b.m.score - a.m.score || a.f.localeCompare(b.f))
        .map(({ f, m }) => ({
          key: f,
          badge: "≡",
          label: f,
          labelIndices: m.indices,
          apply: spliceQuoted(curveTarget, f, 1),
        }));
    } else if (fnTarget) {
      items = matchFunctions(fnTarget.prefix, FUNCTION_NAMES).map((name) => ({
        key: name,
        badge: "fx",
        label: name,
        labelIndices: fuzzyMatch(fnTarget.prefix, name)?.indices,
        detail: FUNCTIONS[name]!.description,
        apply: () => applyCompletion(value, fnTarget, name),
      }));
    }
  }
  const open = items.length > 0;
  const selected = Math.min(selectedIdx, items.length - 1);

  const sig = focused && !disabled && !open ? signatureContext(value, caret) : null;
  const sigDef = sig ? FUNCTIONS[sig.name] : undefined;
  const sigParamIdx = sig && sigDef ? Math.min(sig.argIndex, sigDef.params.length - 1) : -1;
  const callArgs = sig?.name === "CURVE" ? curveCallArgs(value, caret) : null;
  const sigProduct = callArgs?.product ? products.find((p) => p.id === callArgs.product) : undefined;

  const anchor = open || sigDef ? hostRef.current?.getBoundingClientRect() : undefined;

  const accept = (item: CompletionItem) => {
    const view = viewRef.current;
    if (!view) return;
    const applied = item.apply();
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: applied.text },
      selection: { anchor: applied.caret },
    });
    setSelectedIdx(0);
    view.focus();
  };

  const latest = useRef({ value, disabled, inputRef, products, items, selected, open, activePrefix, accept });
  latest.current = { value, disabled, inputRef, products, items, selected, open, activePrefix, accept };
  const callbacks = useRef({ onChange, onCaretChange, onCommit, onCancel, onFocus, onBlur });
  callbacks.current = { onChange, onCaretChange, onCommit, onCancel, onFocus, onBlur };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const chipPlugin = ViewPlugin.fromClass(
      class {
        decorations: DecorationSet;
        atomics: DecorationSet;

        constructor(view: EditorView) {
          ({ decorations: this.decorations, atomics: this.atomics } = buildDecorations(
            view.state.doc.toString(),
            latest.current.products,
          ));
        }

        update(update: ViewUpdate) {
          ({ decorations: this.decorations, atomics: this.atomics } = buildDecorations(
            update.state.doc.toString(),
            latest.current.products,
          ));
        }
      },
      {
        decorations: (v) => v.decorations,
        provide: (plugin) => EditorView.atomicRanges.of((view) => view.plugin(plugin)?.atomics ?? Decoration.none),
      },
    );

    const singleLine = EditorState.transactionFilter.of((tr) => (tr.newDoc.lines > 1 ? [] : tr));

    const acceptSelected = () => {
      const { open: isOpen, items: list, selected: sel, accept: apply } = latest.current;
      if (!isOpen) return false;
      apply(list[sel]!);
      return true;
    };
    const step = (delta: number) => () => {
      const { open: isOpen, items: list, selected: sel } = latest.current;
      if (!isOpen) return false;
      setSelectedIdx((sel + delta + list.length) % list.length);
      return true;
    };
    const commit = (movement: CommitMovement) => () => {
      callbacks.current.onCommit?.(movement);
      return true;
    };
    const keys = Prec.high(
      keymap.of([
        { key: "ArrowDown", run: step(1) },
        { key: "ArrowUp", run: step(-1) },
        { key: "Enter", run: () => acceptSelected() || commit([0, 1])() },
        { key: "Shift-Enter", run: () => acceptSelected() || commit([0, -1])() },
        { key: "Tab", run: () => acceptSelected() || commit([1, 0])() },
        { key: "Shift-Tab", run: () => acceptSelected() || commit([-1, 0])() },
        {
          key: "Escape",
          run: () => {
            const { open: isOpen, activePrefix: prefix } = latest.current;
            if (isOpen) setDismissedPrefix(prefix);
            else callbacks.current.onCancel?.();
            return true;
          },
        },
      ]),
    );

    const listener = EditorView.updateListener.of((update) => {
      if (update.docChanged && !syncingRef.current) {
        callbacks.current.onChange?.(update.state.doc.toString());
        setSelectedIdx(0);
        setDismissedPrefix(null);
      }
      if (update.docChanged || update.selectionSet) {
        const pos = update.state.selection.main.head;
        setCaret(pos);
        if (!syncingRef.current) callbacks.current.onCaretChange?.(pos);
      }
      if (update.focusChanged) {
        setFocused(update.view.hasFocus);
        if (update.view.hasFocus) callbacks.current.onFocus?.();
        else callbacks.current.onBlur?.();
      }
    });

    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: latest.current.value,
        selection: { anchor: latest.current.value.length },
        extensions: [
          singleLine,
          keys,
          history(),
          keymap.of([
            { key: "Backspace", run: deleteCharBackward },
            { key: "Delete", run: deleteCharForward },
            ...historyKeymap,
          ]),
          listener,
          THEME,
          chipPlugin,
          editableRef.current.of([
            EditorView.editable.of(!latest.current.disabled),
            EditorState.readOnly.of(latest.current.disabled),
          ]),
        ],
      }),
    });
    viewRef.current = view;
    const head = view.state.selection.main.head;
    setCaret(head);
    callbacks.current.onCaretChange?.(head);
    latest.current.inputRef?.({
      focus: () => view.focus(),
      blur: () => view.contentDOM.blur(),
      setSelectionRange: (start, end) => {
        const max = view.state.doc.length;
        view.dispatch({ selection: { anchor: Math.min(start, max), head: Math.min(end, max) } });
      },
    });
    return () => {
      latest.current.inputRef?.(null);
      viewRef.current = null;
      view.destroy();
    };
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (view && view.state.doc.toString() !== value) {
      syncingRef.current = true;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: value },
        selection: { anchor: value.length },
      });
      syncingRef.current = false;
    }
  }, [value]);

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: editableRef.current.reconfigure([EditorView.editable.of(!disabled), EditorState.readOnly.of(disabled)]),
    });
  }, [disabled]);

  useEffect(() => {
    latest.current.products = products;
    viewRef.current?.dispatch({});
  }, [products]);

  return (
    <div
      data-artis-ui
      className={`relative ${className}`}
      onKeyDown={(e) => {
        if (e.defaultPrevented && ["Enter", "Tab", "Escape", "ArrowDown", "ArrowUp"].includes(e.key)) {
          e.stopPropagation();
        }
      }}
    >
      <div ref={hostRef} className={`h-full w-full ${disabled ? "bg-[var(--colour-surface-disabled)]" : ""}`} />
      {open &&
        anchor &&
        createPortal(
          <ul
            data-testid="scratchpad-suggestions"
            data-artis-ui
            style={{ position: "fixed", left: anchor.left, top: anchor.bottom, zIndex: 10000 }}
            className="max-h-48 w-80 overflow-auto border border-[var(--colour-border-default)] bg-[var(--colour-surface-container-default)] py-1 text-[12px] text-[var(--colour-text-default)] shadow-md"
          >
            {items.map((item, i) => (
              <li key={item.key}>
                <button
                  type="button"
                  data-testid="scratchpad-suggestion"
                  data-name={item.label}
                  data-selected={i === selected ? "true" : "false"}
                  className={`flex w-full items-baseline gap-1.5 px-2 py-0.5 text-left ${i === selected ? "bg-[var(--colour-surface-primary-tonal)]" : "hover:bg-[var(--colour-surface-hover)]"}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    accept(item);
                  }}
                >
                  {item.badge && (
                    <span className="w-4 shrink-0 text-[var(--colour-text-lighter)] italic">{item.badge}</span>
                  )}
                  <span className="font-medium">
                    <Highlighted text={item.label} indices={item.labelIndices} />
                  </span>
                  {item.detail && (
                    <span className="truncate text-[var(--colour-text-light)]">
                      <Highlighted text={item.detail} indices={item.detailIndices} />
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
      {!open &&
        sig &&
        sigDef &&
        anchor &&
        createPortal(
          <div
            data-testid="scratchpad-signature"
            data-artis-ui
            style={{ position: "fixed", left: anchor.left, top: anchor.bottom, zIndex: 10000 }}
            className="max-w-md border border-[var(--colour-border-default)] bg-[var(--colour-surface-container-default)] px-2 py-1 text-[12px] text-[var(--colour-text-default)] shadow-md"
          >
            {sigProduct && (
              <div className="mb-0.5 flex items-baseline gap-1.5 border-[var(--colour-border-default)] border-b pb-0.5">
                <span className="font-medium">{sigProduct.name}</span>
                {sigProduct.description && (
                  <span className="truncate text-[var(--colour-text-light)]">{sigProduct.description}</span>
                )}
              </div>
            )}
            <div className="whitespace-nowrap">
              <span className="font-semibold">{sig.name}</span>(
              {sigDef.params.map((p, i) => (
                <span key={p}>
                  {i > 0 && <span className="text-[var(--colour-text-light)]">, </span>}
                  <span
                    data-param={p}
                    data-active={i === sigParamIdx ? "true" : "false"}
                    className={
                      i === sigParamIdx
                        ? "rounded bg-[var(--colour-surface-primary-tonal)] font-semibold text-[var(--colour-text-primary-on-tonal)]"
                        : "text-[var(--colour-text-light)]"
                    }
                  >
                    {p}
                  </span>
                </span>
              ))}
              )
            </div>
            <div className="text-[var(--colour-text-light)]">{sigDef.description}</div>
          </div>,
          document.body,
        )}
    </div>
  );
}
